import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@appkit4/react-components/button';
import { UserContext } from '@contexts/UserProvider';
import * as $ from 'jquery';
import styles from './SSOLogin.module.scss';
import { checkEnv, DEMO_USER, clearAllAuthState } from '@utils/shared';
import { getUserProfile } from '@config/getUserProfileApis';
import { TokenContext } from '@contexts/TokenProvider';

const SSOLogin = () => {
    const navigate = useNavigate();

    const { setUser, setUserEmail, setUserGivenName, setUserLastName, clearUser } = useContext(UserContext);
    const { setShowLogin } = useContext(TokenContext);

    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                console.log('[SSO] Page restored from bfcache, reloading...');
                window.location.reload();
            }
        };
        window.addEventListener('pageshow', handlePageShow);

        const queryParams = new URLSearchParams(window.location.search);
        const authError = queryParams.get('auth_error');
        const accessToken = queryParams.get('idToken');

        if (authError) {
            console.warn('[SSO] Auth error received:', authError);
        }

        if (window.location.search || window.history.length > 1) {
            window.history.replaceState({ loginPage: true }, document.title, window.location.pathname);
        }

        const isUserLoggedIn = localStorage.getItem('isUserLoggedIn');

        if (isUserLoggedIn) {
            const userStr = localStorage.getItem('ads.loggedInUser');
            if (userStr) {
                try {
                    const user = JSON.parse(userStr);
                    setUser(user);
                    setUserGivenName(user.given_name);
                    setUserLastName(user.family_name);
                    setUserEmail(user.email);
                    navigate('/dashboard');
                } catch (e) {
                    console.warn('Failed to parse stored user data, clearing invalid session', e);
                    clearUser();
                }
            } else {
                clearUser();
            }
            return;
        }

        const handleUserIAM = async () => {
            try {
                const response = await getUserProfile();
                if (response && response.success) {
                    // Handle successful response
                    const user = response?.data?.resultObject || {};
                    setUser(user);
                    setUserGivenName(user.given_name);
                    setUserLastName(user.family_name);
                    setUserEmail(user.email);

                    localStorage.setItem('isUserLoggedIn', 'true');
                    localStorage.setItem('ads.loggedInUser', JSON.stringify(user));

                    navigate('/dashboard');
                    setShowLogin(true);
                } else {
                    console.error('User profile verification failed:', response?.error);
                    clearUser();
                }
            } catch (error) {
                console.error('Error verifying token:', error);
                clearUser();
            }
        };

        if (accessToken) {
            setShowLogin(false);
            localStorage.setItem('idToken', accessToken || '');
            handleUserIAM();
        }

        return () => {
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: Setter functions from context are stable - this effect should only run on mount

    const handleLogin = async (isRetry = false) => {
        const env = checkEnv();
        if (env === 'local') {
            setUser(DEMO_USER);
            setUserGivenName(String(DEMO_USER.given_name));
            setUserLastName(String(DEMO_USER.family_name));
            setUserEmail(String(DEMO_USER.email));
            navigate('/dashboard');
        } else {
            clearUser();
            clearAllAuthState();

            const timestamp = Date.now();
            const loginUrl = `${window.location.origin}/rapidcraft-service/api/iam/fedLogin?appType=rapidcraft&_t=${timestamp}`;

            $.ajax({
                url: loginUrl,
                type: 'GET',
                cache: false,
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                },
                success: (data: any) => {
                    const authUrl = unescape(data);
                    window.location.replace(encodeURI(authUrl));
                },
                error: (error: any) => {
                    console.error('[SSO] Failed to get login URL:', error);
                    if (error.status === 400 && !isRetry) {
                        console.log('[SSO] Got 400 error, clearing state and retrying...');
                        clearAllAuthState();
                        setTimeout(() => {
                            handleLogin(true);
                        }, 500);
                    } else if (error.status === 400 && isRetry) {
                        console.log('[SSO] Retry failed, reloading page...');
                        window.location.reload();
                    }
                },
            });
        }
    };

    return (
        <div className={styles['sso-login']}>
            <div className={styles.wrapper}>
                <p>Welcome to</p>
                <h1>Design Draft</h1>
                <span>The Future of Design</span>

                <Button kind="primary" onClick={handleLogin} id="sso-login-button">
                    Continue to SSO Login
                </Button>
            </div>
        </div>
    );
};

export default SSOLogin;
